import { action, observable, computed, reaction, makeObservable } from 'mobx';
import {
  uniqBy,
  uniq,
  reduce,
  find,
  filter,
  includes,
  isEmpty,
  debounce,
  isArray,
  unionWith,
  flatten,
  union,
  isEqual,
  remove,
  map,
  get,
  pick,
  groupBy,
  toLower,
  toUpper,
  sortBy,
  partition,
  some,
  upperFirst,
  isNil,
} from 'lodash';
import moment from 'moment';
import qs from 'qs';
import { v4 as uuidv4 } from 'uuid';
import { message } from 'antd';
import axios from 'axios';
import numeral from 'numeral';
import * as Sentry from '@sentry/browser';
import { t } from 'i18next';

import { permissions } from '@/constants';
import { DeviceType, compressorTypes } from '@/constants/deviceTypeEnum';
import { AlertLevels, DataType, InsightType, ProactiveEngineStateEnum } from '@/constants/enums';
import { compressorAlertStatus, getSeriesColorMapping } from '@/comp/InvestigationDashboard/TabPane/Graph';
import epaxios from '@/utils/epaxios';
import i18n from '@/i18n/i18n';
import download from '@/utils/download';
import { token } from '@/theme';
import { getClientId } from '@/sockets/socket';

import RootStore from '../RootStore';
import { getEngineStatusColor } from '../Engine/utils';
import { type EngineStatusLogRecord, normalizeResponse, getEngineStateChangePoints } from './utils';

const INVESTIGATION_AVAILABLE_METRICS_ENDPOINT = (stationId: string) =>
  `investigation-admin/station/${stationId}/available-metrics`;

interface Range {
  stationId: string;
  deviceId: string;
  device1Id: string;
  device2Id: string;
  metric: string;
}

export const noAlert = 'noAlert';

export default class InvestigationStore {
  rootStore: typeof RootStore;
  getDebounced;
  getAlertsDebounced;
  getPressureDropDataDebounced;
  getDeviceMetricsAggregationDataDebounced;
  getDeviceStatusDebounced;
  getProactiveEngineLogsDebounced;
  getEngineStatusesTimelineDebounced;
  getPeriodicLogsDebounced;
  @observable dates1 = null;
  @observable dates2 = null;
  @observable alerts = [];
  @observable cache = []; /*[
    {
      startDate: '2020-08-01T21:00:00Z',
      endDate: '2020-08-08T20:59:59Z',
      resolution(minutes): 10,
      data: [
        {
          deviceId: '294'
          metric: 'currentAmp'
          series: [['2020-08-08T20:59:59Z', 10], [...]]
        }
      ]
    },
    {...}
    ];*/
  @observable devicesMetrics = [];
  @observable stationMetrics = [];
  @observable allowedMetrics = [];
  @observable pressureDrops = [];
  @observable presentedAlerts = [];
  @observable deviceStatus = [];
  @observable deviceStatusSeries = [];
  @observable deviceStatus2 = [];
  @observable activeTab = null;
  @observable deviceMetricsAggregationData = {};
  @observable proactiveEngineLogs = [];
  @observable proactiveEngineLogs2 = [];
  @observable proactiveEngineLogsSeries;
  @observable proactiveEngineLogsSeries2;
  @observable engineStateChangePoints = [];
  @observable engineStatusesTimeline: EngineStatusLogRecord[] = [];
  @observable periodicLogs = [];
  @observable isLoading = false;
  @observable isLoadingDeviceMetricsAggregationData = false;
  @observable isCompareMode = false;
  @observable isExportingData = false;
  @observable isLoadingAvailableMetrics = false;
  @observable showEngineStatus = false;
  cancelRequest;
  cancelAlertRequest;
  cancelPressureDropRequest;
  cancelDevicesMetricsAggregationDataRequest;
  cancelEngineStatusesTimelineRequest;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.getDebounced = debounce(this.get, 500);
    this.getAlertsDebounced = debounce(this.getAlerts, 500);
    this.getPressureDropDataDebounced = debounce(this.getPressureDropData, 500);
    this.getDeviceMetricsAggregationDataDebounced = debounce(this.getDeviceMetricsAggregationData, 500);
    this.getDeviceStatusDebounced = debounce(this.getDeviceStatus, 500);
    this.getProactiveEngineLogsDebounced = debounce(this.getProactiveEngineLogs, 500);
    this.getEngineStatusesTimelineDebounced = debounce(this.getEngineStatusesTimeline, 500);
    this.getPeriodicLogsDebounced = debounce(this.getPeriodicLogs, 500);

    reaction(
      () => this.rootStore.stationStore.currentStation.id,
      () => {
        this.loadData();
        this.reset();
      },
    );

    reaction(
      () => this.rootStore.stationStore.isLoading,
      isStationStoreLoading => {
        if (!isStationStoreLoading) {
          void this.initAvailableMetrics();
        }
      },
    );

    reaction(
      () => this.rootStore.devicesStore.isReady,
      isReady => {
        if (isReady) {
          this.devicesMetrics = this.getDevicesMetrics();
          this.stationMetrics = this.getStationMetrics();
        }
      },
    );

    reaction(
      () => this.rootStore.devicesStore.pressureDropDevices,
      () => {
        this.pressureDrops = this.getPressureDrops();
      },
    );

    reaction(
      () => this.queryParams,
      ({ startDates, endDates, devices, metrics, stationKPIs }) => {
        // cancel all previous get requests
        this.cancelRequest && this.cancelRequest();
        this.cancelAlertRequest && this.cancelAlertRequest();

        // avoid fetching graph data if not enough params
        if (isEmpty(startDates)) {
          this.getDebounced.cancel();
          this.getAlertsDebounced.cancel();
          return;
        }
        if (isEmpty(endDates)) {
          this.getDebounced.cancel();
          this.getAlertsDebounced.cancel();
          return;
        }
        if (!((!isEmpty(devices) && !isEmpty(metrics)) || !isEmpty(stationKPIs))) {
          this.getDebounced.cancel();
          this.getAlertsDebounced.cancel();
          return;
        }

        this.getDebounced();
        this.getAlertsDebounced();
      },
    );

    reaction(
      () => this.pressureDropQueryParams,
      ({ startDates, endDates, devices }) => {
        // cancel all previous get requests
        this.cancelPressureDropRequest && this.cancelPressureDropRequest();

        // avoid fetching graph data if not enough params
        if (isEmpty(startDates)) {
          this.getPressureDropDataDebounced.cancel();
          return;
        }
        if (isEmpty(endDates)) {
          this.getPressureDropDataDebounced.cancel();
          return;
        }

        if (isEmpty(devices)) {
          this.getPressureDropDataDebounced.cancel();
          return;
        }

        this.getPressureDropDataDebounced();
      },
    );

    reaction(
      () => ({
        startDates: this.startDates,
        endDates: this.endDates,
        devices: this.devices,
        getStationKpis: this.getStationKpis,
      }),
      ({ startDates, endDates, devices, getStationKpis }) => {
        // cancel all previous get requests
        this.cancelDevicesMetricsAggregationDataRequest && this.cancelDevicesMetricsAggregationDataRequest();

        // avoid fetching graph data if not enough params
        if (isEmpty(startDates)) {
          this.getDeviceMetricsAggregationDataDebounced.cancel();
          this.deviceMetricsAggregationData = {};
          return;
        }
        if (isEmpty(endDates)) {
          this.getDeviceMetricsAggregationDataDebounced.cancel();
          this.deviceMetricsAggregationData = {};
          return;
        }

        if (!getStationKpis && isEmpty(devices)) {
          this.getDeviceMetricsAggregationDataDebounced.cancel();
          this.deviceMetricsAggregationData = {};
          return;
        }

        this.getDeviceMetricsAggregationDataDebounced();
        this.getDeviceStatusDebounced();
        this.getProactiveEngineLogsDebounced();
        this.getEngineStatusesTimelineDebounced();
        this.getPeriodicLogsDebounced();
      },
    );

    reaction(
      () => this.data1,
      () => {
        this.getProactiveEngineLogsSeries();
        this.getDevicesStatusSeries();
      },
    );

    reaction(
      () => this.engineStatusesTimeline,
      () => {
        this.recalculateEngineStateChangePoints();
      },
    );

    reaction(
      () => this.deviceStatus,
      () => {
        this.getDevicesStatusSeries();
      },
    );

    reaction(
      () => filter(this.devicesMetrics, device => device.isSelectedInDeviceStatus)?.length,
      () => this.getDeviceStatusDebounced(),
    );
  }

  private getAlertsByStrategyForMultipleDevices(isSelectedFirstInCategory: boolean, rangeAlertRecords, data) {
    const { device1Id, device2Id, metric, dataType } = data;
    const getMethod = isSelectedFirstInCategory
      ? this.getRestStationAlertsForSeries.bind(this)
      : this.getStationAlertsForSeriesByMetric.bind(this);

    if (dataType === DataType.PressureDrop) {
      const pressureDropForDevicePair = [device1Id, device2Id].filter(Boolean);
      const alerts = getMethod(rangeAlertRecords, metric, pressureDropForDevicePair);

      return uniqBy(alerts, 'id');
    }

    const selectedDevices = this.getSelectedDevicesForStationMetrics();
    return getMethod(rangeAlertRecords, metric, selectedDevices);
  }

  private getAlertsByStrategyForSingleDevice(isSelectedFirstInCategory: boolean, rangeAlertRecords, data) {
    const { deviceId, metric } = data;
    return isSelectedFirstInCategory
      ? this.getRestDeviceAlertsForSeries(rangeAlertRecords, deviceId, metric)
      : this.getAlertsForSeriesByDeviceAndMetric(rangeAlertRecords, deviceId, metric);
  }

  private getAlertsByStrategy(data, rangeAlertRecords, isSelectedFirstInCategory) {
    if (data.deviceId) {
      return this.getAlertsByStrategyForSingleDevice(isSelectedFirstInCategory, rangeAlertRecords, data);
    }

    return this.getAlertsByStrategyForMultipleDevices(isSelectedFirstInCategory, rangeAlertRecords, data);
  }

  private getSelectedDevicesForStationMetrics() {
    return uniq(
      filter(this.selectedMetrics, metric => !!metric.deviceId).flatMap(selected => [
        selected.deviceId,
        this.rootStore.devicesStore.getParentDeviceById(selected.deviceId)?.id ?? selected.deviceId,
      ]),
    );
  }

  async loadData() {
    await this.fetchDevicesMetadata();
    this.fetchPressureDrop();
    this.resetStationMetrics();
  }

  @action
  async initAvailableMetrics() {
    const stationId = this.rootStore.stationStore.currentStation.id;
    if (!stationId) return;

    try {
      this.isLoadingAvailableMetrics = true;
      this.allowedMetrics = await this.fetchAvailableMetricsList(stationId);
    } catch (e) {
      this.allowedMetrics = [];
      Sentry.captureException(new Error(`Error when getting available investigation metrics: ${e}`));
    } finally {
      this.isLoadingAvailableMetrics = false;
    }

    this.devicesMetrics = this.getDevicesMetrics();
    this.stationMetrics = this.getStationMetrics();
  }

  private async fetchAvailableMetricsList(stationId: string): Promise<string[]> {
    const { data } = await epaxios.get(INVESTIGATION_AVAILABLE_METRICS_ENDPOINT(stationId));

    return Array.isArray(data) ? data : [];
  }

  @action get = async (stationId = this.rootStore.stationStore.currentStation.id) => {
    this.isLoading = true;

    try {
      const { data }: { data } = await epaxios.get(`investigation-admin/station/${stationId}/deviceMetricsData`, {
        params: this.queryParams,
        paramsSerializer: { serialize: params => qs.stringify(params, { arrayFormat: 'comma' }) },
        cancelToken: new axios.CancelToken(c => {
          this.cancelRequest = c;
        }),
      });

      const typedData = data.map(dataObj => {
        const innerData = dataObj.data.map(innerData => ({ ...innerData, dataType: DataType.Metric }));
        const typedModel = {
          ...dataObj,
          data: innerData,
        };
        return typedModel;
      });

      this.updateCache(typedData);
    } catch (e) {
      if (!axios.isCancel(e)) {
        message.error(i18n.t(`couldn't fetch data`));
        this.cleanSelected();
      }
    } finally {
      this.isLoading = false;
    }
  };

  isExportValid = (dataExportQueryParams, resolution, t) => {
    const firstStartDate = moment(dataExportQueryParams?.startDates[0]);
    const firstEndDate = moment(dataExportQueryParams?.endDates[0]);

    if (resolution === '0.1') {
      if (dataExportQueryParams?.stationKPIs.length) {
        message.error(t('This time interval is not supported when exporting any Station KPIs'));
        return false;
      }
      if (firstEndDate.diff(firstStartDate, 'days') > 30) {
        message.error(t('This time interval is not supported when exporting more than 30 days of data'));
        return false;
      }
    }
    if (firstEndDate.diff(firstStartDate, 'days') > 90) {
      message.error(t('Exporting more than 3 months of data is not supported'));
      return false;
    }

    return true;
  };

  @action requestDataExport = async (resolution, timezone, units, t) => {
    this.isExportingData = true;
    const dataExportQueryParams = this.dataExportQueryParams();

    if (!this.isExportValid(dataExportQueryParams, resolution, t)) {
      this.isExportingData = false;
      return false;
    }

    try {
      const exportParams = {
        ...dataExportQueryParams,
        unitSystem: toLower(units),
        utcTimeZone: timezone,
        dataResolution: resolution,
      };

      const clientId = getClientId();

      const { data }: { data: { errorCode: number } } = await epaxios.get(
        `/station/${this.rootStore.stationStore.currentStation.id}/data-export`,
        {
          params: exportParams,
          headers: clientId ? { 'client-id': clientId } : {},
          paramsSerializer: { serialize: params => qs.stringify(params, { arrayFormat: 'comma' }) },
        },
      );
      if (data.errorCode) {
        // 100: Request exceeded maximum number of rows
        // 101: User has already pending request
        return data.errorCode;
      }
    } catch (e) {
      this.isExportingData = false;
      return 999; // General error
    }
  };

  @action setPresentedAlerts = alerts => {
    this.presentedAlerts = alerts;
  };

  dataExportQueryParams() {
    let startDates = [];
    let endDates = [];
    let devices = [];
    let metrics = [];
    let devicesAndMetrics = [];
    let stationKPIs = [];

    const deviceMetricsFlatten = flatten(this.devicesMetrics.map(device => device.metrics));
    const selectedDeviceMetrics = filter(deviceMetricsFlatten, { isSelected: true });
    selectedDeviceMetrics.forEach(metric => {
      if (this.dates1) {
        const [startDate, endDate] = this.dates1 || [];
        const formattedStartDate = startDate?.toISOString();
        const formattedEndDate = endDate?.toISOString();

        startDates = union(startDates, formattedStartDate ? [formattedStartDate] : []);
        endDates = union(endDates, formattedEndDate ? [formattedEndDate] : []);
        // store id and name in object to avoid duplicates of id/name pairs
        devicesAndMetrics = unionWith(
          devicesAndMetrics,
          [
            {
              id: metric.deviceId,
              name: metric.name,
            },
          ],
          isEqual,
        );
      }

      if (this.dates2) {
        const [startDate, endDate] = this.dates2 || [];
        const formattedStartDate = startDate?.toISOString();
        const formattedEndDate = endDate?.toISOString();

        startDates = union(startDates, formattedStartDate ? [formattedStartDate] : []);
        endDates = union(endDates, formattedEndDate ? [formattedEndDate] : []);
        devicesAndMetrics = unionWith(
          devicesAndMetrics,
          [
            {
              id: metric.deviceId,
              name: metric.name,
            },
          ],
          isEqual,
        );
      }
    });

    devices = devicesAndMetrics.map(({ id }) => id);
    metrics = devicesAndMetrics.map(({ name }) => name);

    const selectedStationMetrics = filter(this.stationMetrics, { isSelected: true });
    selectedStationMetrics.forEach(kpi => {
      if (this.dates1) {
        const [startDate, endDate] = this.utcFormattedDates1;
        startDates = union(startDates, startDate ? [startDate] : []);
        endDates = union(endDates, endDate ? [endDate] : []);
        stationKPIs = union(stationKPIs, kpi.key ? [kpi.key] : []);
      }

      if (this.dates2) {
        const [startDate, endDate] = this.utcFormattedDates2;
        startDates = union(startDates, startDate ? [startDate] : []);
        endDates = union(endDates, endDate ? [endDate] : []);
        stationKPIs = union(stationKPIs, kpi.key ? [kpi.key] : []);
      }
    });

    return {
      startDates,
      endDates,
      devices,
      metrics,
      stationKPIs,
    };
  }

  @action getAlerts = async (stationId = this.rootStore.stationStore.currentStation.id) => {
    try {
      const endPointEnding = this.rootStore.permissionStore.isAuth(permissions.VIEW_INTERNAL_SEVERITY_ALERT)
        ? '/internal'
        : '';

      const { data }: { data } = await epaxios.get(`/alert-record/station/${stationId}/time-periods${endPointEnding}`, {
        params: {
          ...pick(this.queryParams, ['startDates', 'endDates']),
          devices: uniq(this.queryParams.devices),
        },
        paramsSerializer: { serialize: params => qs.stringify(params, { arrayFormat: 'comma' }) },
        cancelToken: new axios.CancelToken(c => {
          this.cancelAlertRequest = c;
        }),
      });

      this.updateAlerts(data.data);
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting alerts: ${e}`));
    }
  };

  @action updateAlertFromServer = (newAlert: { id?: string }) => {
    this.alerts.forEach(alertPeriod => {
      const oldAlert = find(alertPeriod.records, { id: newAlert.id });
      if (oldAlert) {
        Object.assign(oldAlert, newAlert);
      }
    });
  };

  @computed get sortedAlertsByShutdown() {
    return sortBy(this.alerts?.[0]?.records, alert => alert?.severity !== AlertLevels.Shutdown);
  }

  @action getPressureDropData = async (stationId = this.rootStore.stationStore.currentStation.id) => {
    this.isLoading = true;

    try {
      const { data }: { data: { data }[] } = await epaxios.get(
        `investigation-admin/station/${stationId}/pressureDropData`,
        {
          params: {
            ...pick(this.pressureDropQueryParams, ['startDates', 'endDates', 'devices']),
          },
          paramsSerializer: { serialize: params => qs.stringify(params, { arrayFormat: 'comma' }) },
          cancelToken: new axios.CancelToken(c => {
            this.cancelPressureDropRequest = c;
          }),
        },
      );

      const typedData = data.map(dataObj => {
        const innerData = dataObj.data.map(innerData => ({ ...innerData, dataType: DataType.PressureDrop }));
        const typedModel = {
          ...dataObj,
          data: innerData,
        };
        return typedModel;
      });

      this.updateCache(typedData);
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Pressure Drop data: ${e}`));
    } finally {
      this.isLoading = false;
    }
  };

  @action getDeviceMetricsAggregationData = async (stationId = this.rootStore.stationStore.currentStation.id) => {
    this.isLoadingDeviceMetricsAggregationData = true;

    try {
      const { data } = await epaxios.get(
        `investigation-admin/station/${stationId}/analytics/deviceMetricsAggregationData`,
        {
          params: this.devicesMetricsAggregationDataQueryParams,
          paramsSerializer: { serialize: params => qs.stringify(params, { arrayFormat: 'comma' }) },
          cancelToken: new axios.CancelToken(c => {
            this.cancelDevicesMetricsAggregationDataRequest = c;
          }),
        },
      );

      this.deviceMetricsAggregationData = new DeviceMetricsAggregationData(data);
    } catch (e) {
      this.deviceMetricsAggregationData = {};
    } finally {
      this.isLoadingDeviceMetricsAggregationData = false;
    }
  };

  @action updateCache = data => {
    if (!isArray(data)) return;

    data.forEach(newRange => {
      const oldRange = find(this.cache, {
        startDate: newRange.startDate,
        endDate: newRange.endDate,
      });

      if (oldRange) {
        const unionData = unionWith(oldRange.data, newRange.data, (o1: Range, o2: Range) => {
          // handle comparing of stationKPIs
          if ('stationId' in o1) {
            return o1['stationId'] === o2['stationId'] && o1['metric'] === o2['metric'];
          }

          // handle comparing of deviceMetrics
          if ('deviceId' in o1) {
            return o1['deviceId'] === o2['deviceId'] && o1['metric'] === o2['metric'];
          }

          // handle comparing of pressureDrops
          if ('device1Id' in o1) {
            return (
              o1['device1Id'] === o2['device1Id'] &&
              o1['device2Id'] === o2['device2Id'] &&
              o1['metric'] === o2['metric']
            );
          }

          // handle default comparing
          return o1['deviceId'] === o2['deviceId'] && o1['metric'] === o2['metric'];
        });

        oldRange.data = unionData;
      } else {
        this.cache.push(newRange);
      }
    });
  };

  @action updateAlerts = data => {
    if (!isArray(data)) return;

    data.forEach(newRange => {
      const oldRange = find(this.alerts, {
        startDate: newRange.startDate,
        endDate: newRange.endDate,
      });

      if (oldRange) {
        const newRecords = [...oldRange.records, ...newRange.records];
        const newRecordsUnique = uniqBy(newRecords, 'id');

        oldRange.records = newRecordsUnique;
      } else {
        this.alerts.push(newRange);
      }
    });
  };

  @action cleanSelected = () => {
    const deviceMetricsFlatten = flatten(this.devicesMetrics.map(device => device.metrics));
    deviceMetricsFlatten.forEach(metric => {
      if (this.dates1 && !this.isMetricCached(metric, this.dates1)) {
        metric.isSelected = false;
      }

      if (this.dates2 && !this.isMetricCached(metric, this.dates2)) {
        metric.isSelected = false;
      }
    });
  };

  @action setActiveTab = tabKey => {
    this.activeTab = tabKey;
  };

  @action setDates1 = dates => {
    if (isArray(dates)) {
      let [startDate, endDate] = dates;
      const momentStartDate = startDate?._isAMomentObject || isEmpty(startDate) ? startDate : moment(startDate?.$d);
      const momentEndDate = endDate?._isAMomentObject || isEmpty(endDate) ? endDate : moment(endDate?.$d);
      startDate = (momentStartDate?.isDST() ? momentStartDate.add(1, 'hours') : momentStartDate)?.startOf('day');
      endDate = (momentEndDate?.isDST() ? momentEndDate.add(1, 'hours') : momentEndDate)?.endOf('day');

      this.dates1 = [startDate, endDate];
    } else this.dates1 = dates;

    this.removeCachedDates(this.dates1);

    // Only reset dates2 if not in compare mode
    if (!this.isCompareMode) {
      this.removeCachedDates(this.dates2);
      this.dates2 = null;
    }
  };

  @action setDates2 = dates => {
    if (get(dates, '[0]')) {
      const dates1DiffInDays = this.dates1[1].diff(this.dates1[0], 'days');
      let [startDate, endDate] = dates;
      const momentStartDate = startDate?._isAMomentObject || isEmpty(startDate) ? startDate : moment(startDate?.$d);
      startDate = (momentStartDate?.isDST() ? momentStartDate.add(1, 'hours') : momentStartDate)?.startOf('day');
      endDate = startDate.clone().add(dates1DiffInDays, 'days').endOf('day');
      dates = [startDate, endDate];
    } else {
      dates = null;
    }
    this.removeCachedDates(this.dates2);
    this.dates2 = dates;
  };

  @action removeCachedDates = dates => {
    if (!isArray(dates)) return;

    remove(this.cache, {
      startDate: dates[0]?.toISOString(),
      endDate: dates[1]?.toISOString(),
    });
  };

  @action setSelectedMetric = (id, value) => {
    const metrics = reduce(this.devicesMetrics, (result, value) => [...result, ...value.metrics], []);
    const metric = find(metrics, { id });

    if (!metric) return;

    metric.isSelected = value;
  };

  @action setSelectedStationMetric = (id, value) => {
    const metric = find(this.stationMetrics, { id });
    if (!metric) return;

    metric.isSelected = value;
  };

  @action setSelectedPressureDrop = (id, isSelected) => {
    const pressureDrop = find(this.pressureDrops, { id });

    if (pressureDrop) {
      pressureDrop.isSelected = isSelected;
    }
  };

  @action reset = () => {
    this.devicesMetrics = [];
    this.stationMetrics = [];
    this.deviceMetricsAggregationData = {};
    this.cache = [];
    this.alerts = [];
    this.activeTab = null;
    this.dates1 = null;
    this.dates2 = null;
    this.isLoading = false;
    this.isCompareMode = false;
    this.engineStatusesTimeline = [];
    this.showEngineStatus = true;
    this.engineStateChangePoints = [];
    this.cancelEngineStatusesTimelineRequest?.();
  };

  @action resetStationMetrics() {
    this.stationMetrics = this.getStationMetrics();
  }

  @action
  async fetchDevicesMetadata() {
    if (!this.rootStore.stationStore.currentStation.id) return;

    await this.rootStore.devicesStore.getDevicesMetadata();
  }

  @action
  async fetchPressureDrop() {
    if (!this.rootStore.stationStore.currentStation.id) return;

    await this.rootStore.devicesStore.getPressureDropDevices();
  }

  getAllDevicesMetrics() {
    // step 1 - get all devices
    const allDevices = this.rootStore.devicesStore.devicesMetadataWithoutPipes;

    // step 2 - remove station devices
    const withoutStation = filter(allDevices, ({ id }) => id !== 'station');
    const currentStationId = this.rootStore.stationStore.currentStation.id;
    // https://ecoplant.atlassian.net/browse/BAC-8414
    // Station Valent Biosciences | Osage. IA | Main needs to hide the following devices from investigation dashboard:
    const disabledDevices = [
      '074ed173-6d35-4c40-8d9e-a344a87104bf', // #36 - f1
      '63fd076d-db50-4ce1-ab4b-b59522613345', // #35 - f3
      '9e2005af-366e-49b1-8eb6-4a4d4a2c6383', // #37 -Flow - CS VD 500
      'f9476321-b45e-4e7c-a502-8d25e42612c3', // #34 - f2
    ];
    const devices =
      currentStationId === 'a1bae28c-fddd-46db-a139-a73f5ec24c82'
        ? withoutStation.filter(device => !disabledDevices.includes(device.id))
        : withoutStation;
    // step 3 - remove all disallowed metrics
    const withAllowedMetrics = devices.map(device => {
      return {
        ...device,
        metrics: filter(device.metrics, metric => this.allowedMetrics.includes(metric)),
      };
    });

    // step 4 - convert each metric into an object with id and some extra data
    const withMappedMetrics = withAllowedMetrics.map(device => {
      return {
        ...device,
        isSelectedInDeviceStatus: false,
        metrics: device.metrics.map(name => ({
          id: uuidv4(),
          deviceId: device.id,
          name,
          isSelected: false,
          deviceName: device.name,
        })),
      };
    });

    // step 5 - add children ids to each parent device
    const withChildrenIds = withMappedMetrics.map((device, _index, arr) => {
      const childs = filter(arr, { parentDeviceId: device.id });
      const childsIds = childs.map(child => child.id);

      return {
        ...device,
        children: childsIds,
      };
    });

    // step 6 - augment parent's metrics with its children's metrics
    const withChildMetrics = withChildrenIds.map((device, _index, arr) => {
      const childs = filter(arr, { parentDeviceId: device.id });
      const childsMetrics = childs.reduce((acc, child) => [...acc, ...child.metrics], []);

      // filter out parent metric if children has the same metric
      const uniqueDeviceMetrics = device.metrics.filter(
        metric => !childsMetrics.some(childMetric => !isNil(childMetric) && childMetric.name === metric?.name),
      );

      return {
        ...device,
        metrics: [...uniqueDeviceMetrics, ...childsMetrics],
      };
    });

    return withChildMetrics;
  }

  getDevicesMetrics() {
    const withChildMetrics = this.getAllDevicesMetrics();

    // step 7 - remove all children devices
    const withoutChildren = filter(withChildMetrics, ({ parentDeviceId }) => !parentDeviceId);

    // step 8 - remove all devices without metrics
    const withoutEmptyMetrics = filter(withoutChildren, ({ metrics }) => !isEmpty(metrics));

    return withoutEmptyMetrics;
  }

  getPressureDrops() {
    if (!Array.isArray(this.rootStore.devicesStore.pressureDropDevices)) {
      return [];
    }
    const pressureDrops = this.rootStore.devicesStore.pressureDropDevices.map(pressureDrop => {
      const { lowPressureDevice, highPressureDevice } = pressureDrop;
      const highPressureDeviceMetadata = this.rootStore.devicesStore.deviceMetadataFromDeviceId(highPressureDevice);
      const lowPressureDeviceMetadata = this.rootStore.devicesStore.deviceMetadataFromDeviceId(lowPressureDevice);
      const highPressureDeviceName = get(highPressureDeviceMetadata, 'name', highPressureDevice);
      const lowPressureDeviceName = get(lowPressureDeviceMetadata, 'name', lowPressureDevice);

      return {
        ...pressureDrop,
        id: uuidv4(),
        isSelected: false,
        key: `${highPressureDevice}+${lowPressureDevice}`,
        name: `${highPressureDeviceName} - ${lowPressureDeviceName}`,
      };
    });

    return pressureDrops;
  }

  getStationMetrics() {
    const stationDevice = find(this.rootStore.devicesStore.devicesMetadata, { id: 'station' });

    return (
      stationDevice?.metrics.map(metric => {
        return {
          id: uuidv4(),
          key: metric,
          name: this.rootStore.devicesStore.getMetricName(metric),
          isSelected: false,
        };
      }) || []
    );
  }

  @action getCachedPressureDrop = (pressureDrop, dates = []) => {
    const [startDate, endDate] = dates || [];
    const startDateFormatted = startDate?.toISOString();
    const endDateFormatted = endDate?.toISOString();

    const cachedRange =
      find(this.cache, {
        startDate: startDateFormatted,
        endDate: endDateFormatted,
      }) || [];

    const cachedPressureDrop = find(cachedRange.data, {
      device1Id: pressureDrop.highPressureDevice,
      device2Id: pressureDrop.lowPressureDevice,
      metric: 'pressureBar',
    });

    return cachedPressureDrop;
  };

  @action isPressureDropCached = (pressureDrop, dates) => {
    const cachedMetric = this.getCachedPressureDrop(pressureDrop, dates);
    return !!cachedMetric;
  };

  @action getCachedMetric = (metric, dates = []) => {
    const [startDate, endDate] = dates || [];
    const startDateFormatted = startDate?.toISOString();
    const endDateFormatted = endDate?.toISOString();

    const cachedRange =
      find(this.cache, {
        startDate: startDateFormatted,
        endDate: endDateFormatted,
      }) || [];

    const cachedMetric = find(cachedRange.data, {
      deviceId: metric.deviceId,
      metric: metric.name,
    });

    return cachedMetric;
  };

  @action isMetricCached = (metric, dates) => {
    const cachedMetric = this.getCachedMetric(metric, dates);
    return !!cachedMetric;
  };

  @action getCachedStationKPI = (kpi, dates = []) => {
    const [startDate, endDate] = dates || [];
    const startDateFormatted = startDate?.toISOString();
    const endDateFormatted = endDate?.toISOString();

    const cachedRange =
      find(this.cache, {
        startDate: startDateFormatted,
        endDate: endDateFormatted,
      }) || [];

    const cachedKPI = find(cachedRange.data, {
      metric: kpi.key,
      stationId: this.rootStore.stationStore.currentStation.id,
    });

    return cachedKPI;
  };

  @action isStationKPICached = (kpi, dates) => {
    const cachedKPI = this.getCachedStationKPI(kpi, dates);
    return !!cachedKPI;
  };

  @action getDateRounded = (duration, date) => {
    const durationInMilliseconds = duration * 60 * 1000;
    const mDate = moment(date).utc();
    const unitOfDiff = (() => {
      switch (duration) {
        case 1:
        case 10:
        case 30:
        case 60:
          return 'hour';

        case 1440:
          return 'day';

        default:
          return 'hour';
      }
    })();
    const milliseconds = mDate.diff(mDate.clone().startOf(unitOfDiff));

    let numOfDurationUnits = Math.floor(milliseconds / durationInMilliseconds);
    const remainder = milliseconds % durationInMilliseconds;

    // round up if closer to end of duration rather than its start
    if (remainder > durationInMilliseconds / 2) {
      numOfDurationUnits += 1;
    }
    // set date
    const ans = mDate
      .clone()
      .startOf(unitOfDiff)
      .add(numOfDurationUnits * durationInMilliseconds, 'milliseconds');

    // return rounded date in unix time
    return ans.valueOf();
  };

  @action setIsCompareMode = value => {
    this.isCompareMode = value;
  };

  @action setShowEngineStatus = (value: boolean) => {
    this.showEngineStatus = value;
  };

  @computed get hasEngine(): boolean {
    const proactiveEngineState = this.rootStore.proactiveEngineStore?.proactiveEngineState;
    return proactiveEngineState != null && proactiveEngineState !== ProactiveEngineStateEnum.Unavailable;
  }

  @computed get sortedEngineStatusesTimeline(): EngineStatusLogRecord[] {
    if (!this.engineStatusesTimeline?.length) {
      return [];
    }
    return sortBy(this.engineStatusesTimeline, r => moment(r.startDate).valueOf());
  }

  getEngineStatusAtTime(timestamp: number): { status: string; color: string; label: string } | null {
    if (!this.showEngineStatus || !this.sortedEngineStatusesTimeline.length) {
      return null;
    }

    let currentState: EngineStatusLogRecord | null = null;
    for (const record of this.sortedEngineStatusesTimeline) {
      const recordTime = moment(record.startDate).utc().valueOf();
      if (recordTime <= timestamp) {
        currentState = record;
      } else {
        break;
      }
    }

    if (!currentState) {
      return null;
    }

    return {
      status: currentState.state,
      color: getEngineStatusColor(currentState.state),
      label: t(currentState.state),
    };
  }

  @computed get queryParams() {
    let startDates = [];
    let endDates = [];
    let devices = [];
    let metrics = [];
    let devicesAndMetrics = [];
    let stationKPIs = [];

    const deviceMetricsFlatten = flatten(this.devicesMetrics.map(device => device.metrics));
    const selectedDeviceMetrics = filter(deviceMetricsFlatten, { isSelected: true });
    selectedDeviceMetrics.forEach(metric => {
      if (this.dates1 && !this.isMetricCached(metric, this.dates1)) {
        const [startDate, endDate] = this.dates1 || [];
        const formattedStartDate = startDate?.toISOString();
        const formattedEndDate = endDate?.toISOString();

        startDates = union(startDates, formattedStartDate ? [formattedStartDate] : []);
        endDates = union(endDates, formattedEndDate ? [formattedEndDate] : []);
        // store id and name in object to avoid duplicates of id/name pairs
        devicesAndMetrics = unionWith(
          devicesAndMetrics,
          [
            {
              id: metric.deviceId,
              name: metric.name,
            },
          ],
          isEqual,
        );
      }

      if (this.dates2 && !this.isMetricCached(metric, this.dates2)) {
        const [startDate, endDate] = this.dates2 || [];
        const formattedStartDate = startDate?.toISOString();
        const formattedEndDate = endDate?.toISOString();

        startDates = union(startDates, formattedStartDate ? [formattedStartDate] : []);
        endDates = union(endDates, formattedEndDate ? [formattedEndDate] : []);
        devicesAndMetrics = unionWith(
          devicesAndMetrics,
          [
            {
              id: metric.deviceId,
              name: metric.name,
            },
          ],
          isEqual,
        );
      }
    });

    devices = devicesAndMetrics.map(({ id }) => id);
    metrics = devicesAndMetrics.map(({ name }) => name);

    const selectedStationMetrics = filter(this.stationMetrics, { isSelected: true });
    selectedStationMetrics.forEach(kpi => {
      if (this.dates1 && !this.isStationKPICached(kpi, this.dates1)) {
        const [startDate, endDate] = this.utcFormattedDates1;
        startDates = union(startDates, startDate ? [startDate] : []);
        endDates = union(endDates, endDate ? [endDate] : []);
        stationKPIs = union(stationKPIs, kpi.key ? [kpi.key] : []);
      }

      if (this.dates2 && !this.isStationKPICached(kpi, this.dates2)) {
        const [startDate, endDate] = this.utcFormattedDates2;
        startDates = union(startDates, startDate ? [startDate] : []);
        endDates = union(endDates, endDate ? [endDate] : []);
        stationKPIs = union(stationKPIs, kpi.key ? [kpi.key] : []);
      }
    });

    return {
      startDates,
      endDates,
      devices,
      metrics,
      stationKPIs,
    };
  }

  @computed get pressureDropQueryParams() {
    const startDates = [];
    const endDates = [];
    let devices = [];
    let devicesPairs = [];

    this.selectedPressureDrops.forEach(pressureDrop => {
      if (this.dates1 && !this.isPressureDropCached(pressureDrop, this.dates1)) {
        const [startDate, endDate] = this.dates1 || [];
        const formattedStartDate = startDate?.toISOString();
        const formattedEndDate = endDate?.toISOString();
        const { highPressureDevice, lowPressureDevice } = pressureDrop;

        formattedStartDate && !includes(startDates, formattedStartDate) && startDates.push(formattedStartDate);
        formattedEndDate && !includes(endDates, formattedEndDate) && endDates.push(formattedEndDate);
        devicesPairs = unionWith(devicesPairs, [[highPressureDevice, lowPressureDevice]], isEqual);
      }

      if (this.dates2 && !this.isPressureDropCached(pressureDrop, this.dates2)) {
        const [startDate, endDate] = this.dates2 || [];
        const formattedStartDate = startDate?.toISOString();
        const formattedEndDate = endDate?.toISOString();
        const { highPressureDevice, lowPressureDevice } = pressureDrop;

        formattedStartDate && !includes(startDates, formattedStartDate) && startDates.push(formattedStartDate);
        formattedEndDate && !includes(endDates, formattedEndDate) && endDates.push(formattedEndDate);
        devicesPairs = unionWith(devicesPairs, [[highPressureDevice, lowPressureDevice]], isEqual);
      }
    });

    // flatten devicesPairs into one long array of devices id's
    devices = flatten(devicesPairs);

    return {
      startDates,
      endDates,
      devices,
    };
  }

  @computed get devicesMetricsAggregationDataQueryParams() {
    const startDates = [];
    const endDates = [];

    // sort dates from present to past before sending to server
    const dates1startDate = get(this.dates1, '[0]');
    const dates2startDate = get(this.dates2, '[0]');
    const laterPeriod = dates1startDate?.isBefore(dates2startDate) ? this.dates2 : this.dates1;
    const formerPeriod = dates1startDate?.isBefore(dates2startDate) ? this.dates1 : this.dates2;

    if (formerPeriod) {
      const [startDate, endDate] = formerPeriod;
      const formattedStartDate = startDate?.toISOString();
      const formattedEndDate = endDate?.toISOString();

      formattedStartDate && startDates.push(formattedStartDate);
      formattedEndDate && endDates.push(formattedEndDate);
    }

    if (laterPeriod) {
      const [startDate, endDate] = laterPeriod;
      const formattedStartDate = startDate?.toISOString();
      const formattedEndDate = endDate?.toISOString();

      formattedStartDate && startDates.push(formattedStartDate);
      formattedEndDate && endDates.push(formattedEndDate);
    }

    const devices = this.devicesMetrics
      .filter(device => device.metrics.some(({ isSelected }) => isSelected))
      .map(device => device.id);

    const getStationKpis = this.stationMetrics.some(metric => metric.isSelected);

    return {
      startDates,
      endDates,
      devices,
      getStationKpis,
    };
  }

  @computed get startDates(): string[] {
    const startDateToISO = (dates: Date[]): string | null => dates?.[0]?.toISOString() ?? null;

    const dates1StartDate = get(this.dates1, '[0]');
    const dates2StartDate = get(this.dates2, '[0]');

    const [formerPeriod, laterPeriod] = dates1StartDate?.isBefore(dates2StartDate)
      ? [this.dates1, this.dates2]
      : [this.dates2, this.dates1];

    return [formerPeriod, laterPeriod].map(startDateToISO).filter(Boolean) as string[];
  }

  @computed get endDates(): string[] {
    const endDateToISO = (dates: Date[]): string | null => dates?.[1]?.toISOString() ?? null;

    const dates1StartDate = get(this.dates1, '[0]');
    const dates2StartDate = get(this.dates2, '[0]');

    const [formerPeriod, laterPeriod] = dates1StartDate?.isBefore(dates2StartDate)
      ? [this.dates1, this.dates2]
      : [this.dates2, this.dates1];

    return [formerPeriod, laterPeriod].map(endDateToISO).filter(Boolean) as string[];
  }

  @computed get devices(): string[] {
    return this.devicesMetrics
      .filter(device => device.metrics.some(({ isSelected }) => isSelected))
      .map(device => device.id);
  }

  @computed get getStationKpis(): boolean {
    return this.stationMetrics.some(metric => metric.isSelected);
  }

  @computed get totalMetricsSelected() {
    const totalDevicesMetrics = reduce(
      this.devicesMetrics,
      (result, value) => {
        const totalSelected = filter(value.metrics, { isSelected: true }).length;
        return result + totalSelected;
      },
      0,
    );

    const totalStationMetrics = filter(this.stationMetrics, { isSelected: true }).length;
    const totalPressureDrops = filter(this.pressureDrops, { isSelected: true }).length;

    const total = totalDevicesMetrics + totalStationMetrics + totalPressureDrops;

    return total;
  }

  @computed get data1() {
    const newData = [];
    const [startDate, endDate] = this.utcFormattedDates1;
    const cachedRange =
      find(this.cache, {
        startDate,
        endDate,
      }) || {};
    const alertsRange =
      find(this.alerts, {
        startDate,
        endDate,
      }) || {};

    // get devicesMetrics relevant data from cache
    this.devicesMetrics.forEach(device => {
      const selectedDeviceMetrics = filter(device.metrics, { isSelected: true });
      selectedDeviceMetrics.forEach((metric, index) => {
        const cachedMetric = this.getCachedMetric(metric, this.dates1);

        if (cachedMetric) {
          newData.push({ ...cachedMetric, isSelectedFirstInCategory: index === 0 });
        }
      });
    });

    // get stationKPIs relevant data from cache
    const selectedStationMetrics = filter(this.stationMetrics, { isSelected: true });
    selectedStationMetrics.forEach((kpi, index) => {
      const cachedStationKPI = this.getCachedStationKPI(kpi, this.dates1);

      if (cachedStationKPI) {
        newData.push({ ...cachedStationKPI, isSelectedFirstInCategory: index === 0 });
      }
    });

    // get pressureDrop relevant data from cache
    this.selectedPressureDrops.forEach(pressureDrop => {
      const cachedPressureDrop = this.getCachedPressureDrop(pressureDrop, this.dates1);

      if (cachedPressureDrop) {
        newData.push(cachedPressureDrop);
      }
    });

    const allDevicesMetrics = this.getAllDevicesMetrics();

    return {
      ...cachedRange,
      data: newData.map(data => ({
        ...data,
        deviceName:
          find(allDevicesMetrics, { id: data.deviceId })?.name ||
          find(this.pressureDrops, {
            highPressureDevice: data.device1Id,
            lowPressureDevice: data.device2Id,
          })?.name ||
          find(allDevicesMetrics, device => device.children.includes(data.deviceId))?.name ||
          (data.stationId && 'Station KPIs'),
        alerts: this.getAlertsForSeries(data, alertsRange.records, cachedRange),
        series: data?.series?.map(([date, value]) => [Date.parse(date), value]),
      })),
    };
  }

  @computed get data2() {
    const newData = [];
    const [startDate, endDate] = this.utcFormattedDates2;
    const cachedRange =
      find(this.cache, {
        startDate,
        endDate,
      }) || {};
    const alertsRange =
      find(this.alerts, {
        startDate: startDate,
        endDate: endDate,
      }) || {};

    // get devicesMetrics relevant data from cache
    this.devicesMetrics.forEach(device => {
      const selectedDeviceMetrics = filter(device.metrics, { isSelected: true });
      selectedDeviceMetrics.forEach((metric, index) => {
        const cachedMetric = this.getCachedMetric(metric, this.dates2);

        if (cachedMetric) {
          newData.push({ ...cachedMetric, isSelectedFirstInCategory: index === 0 });
        }
      });
    });

    // get stationKPIs relevant data from cache
    const selectedStationMetrics = filter(this.stationMetrics, { isSelected: true });

    selectedStationMetrics.forEach((kpi, index) => {
      const cachedStationKPI = this.getCachedStationKPI(kpi, this.dates2);

      if (cachedStationKPI) {
        newData.push({ ...cachedStationKPI, isSelectedFirstInCategory: index === 0 });
      }
    });

    // get pressureDrop relevant data from cache
    this.selectedPressureDrops.forEach(pressureDrop => {
      const cachedPressureDrop = this.getCachedPressureDrop(pressureDrop, this.dates2);

      if (cachedPressureDrop) {
        newData.push(cachedPressureDrop);
      }
    });

    return {
      ...cachedRange,
      data: newData.map(data => ({
        ...data,
        deviceName:
          find(this.devicesMetrics, { id: data.deviceId })?.name ||
          find(this.pressureDrops, {
            highPressureDevice: data.device1Id,
            lowPressureDevice: data.device2Id,
          })?.name ||
          find(this.devicesMetrics, device => {
            return device.children.includes(data.deviceId);
          })?.name ||
          (data.stationId && 'Station KPIs'),
        alerts: this.getAlertsForSeries(data, alertsRange.records, cachedRange),
        series: data?.series?.map(([date, value]) => [Date.parse(date), value]),
      })),
    };
  }

  getAlertsForSeries(data, rangeAlertRecords, cachedRange) {
    const { isSelectedFirstInCategory, dataType } = data;

    if (dataType !== DataType.Metric && dataType !== DataType.PressureDrop) {
      return [];
    }

    const allAlerts = this.getAlertsByStrategy(data, rangeAlertRecords, isSelectedFirstInCategory);

    allAlerts.forEach(alert => {
      alert.openingTimeRounded = this.getDateRounded(cachedRange['resolution(minutes)'], alert.openingTime);
    });

    return allAlerts;
  }

  getAlertsForSeriesByDeviceAndMetric(rangeAlertRecords, deviceId, metricName) {
    return filter(rangeAlertRecords, ({ alertType, affectedDevicesWithAttributes, metricNames }) => {
      return (
        alertType !== 'manufacturer' && // manufacturer alerts doesn't have metrics defined
        includes(metricNames, metricName) &&
        this.isDeviceIncludedInAffectedDevicesList(affectedDevicesWithAttributes, deviceId)
      );
    });
  }

  getStationAlertsForSeriesByMetric(rangeAlertRecords, metricName, selectedDevices) {
    return filter(rangeAlertRecords, ({ alertType, affectedDevicesWithAttributes, metricNames }) => {
      return (
        alertType !== 'manufacturer' && // manufacturer alerts doesn't have metrics defined
        includes(metricNames, metricName) &&
        this.isAnySelectedDeviceRelatesToAffectedDevices(affectedDevicesWithAttributes, selectedDevices)
      );
    });
  }

  getRestDeviceAlertsForSeries(rangeAlertRecords, deviceId, metricName) {
    const selectedMetricsForThisDeviceExceptThisMetric = filter(this.selectedMetrics, { deviceId: deviceId })
      .filter(deviceMetric => deviceMetric.name !== metricName)
      .map(deviceMetric => deviceMetric.name);
    return filter(rangeAlertRecords, ({ affectedDevicesWithAttributes, metricNames = [] }) => {
      return (
        metricNames.every(metric => !selectedMetricsForThisDeviceExceptThisMetric.includes(metric)) &&
        this.isDeviceIncludedInAffectedDevicesList(affectedDevicesWithAttributes, deviceId)
      );
    });
  }

  getRestStationAlertsForSeries(rangeAlertRecords, metricName, selectedDevices) {
    const selectedMetricsForStationExceptThisMetric = this.stationMetrics.filter(
      kpiMetric => kpiMetric.metric !== metricName,
    );
    return filter(rangeAlertRecords, ({ affectedDevicesWithAttributes, metricNames = [] }) => {
      return (
        metricNames.every(metric => !selectedMetricsForStationExceptThisMetric.includes(metric)) &&
        !this.isAnySelectedDeviceRelatesToAffectedDevices(affectedDevicesWithAttributes, selectedDevices)
      );
    });
  }

  isDeviceIncludedInAffectedDevicesList(affectedDevices, deviceId) {
    return (
      find(affectedDevices, { id: deviceId }) ||
      find(affectedDevices, { id: this.rootStore.devicesStore.getParentDeviceById(deviceId)?.id })
    );
  }

  isAnySelectedDeviceRelatesToAffectedDevices(affectedDevices, selectedDevicesIds) {
    return affectedDevices.some(device => selectedDevicesIds.includes(device.id));
  }

  @computed get utcFormattedDates1() {
    return map(this.dates1, date => date?.toISOString());
  }

  @computed get utcFormattedDates2() {
    return map(this.dates2, date => date?.toISOString());
  }

  @computed get maxAllowedSelected() {
    return this.dates2 ? 5 : 10;
  }

  @computed get allowStacking() {
    let allow = true;

    this.devicesMetrics.forEach(device => {
      device.metrics.forEach(metric => {
        const { isSelected, name } = metric;
        if (isSelected && name !== 'powerKw') {
          allow = false;
        }
      });
    });

    this.stationMetrics.forEach(station => {
      const { isSelected } = station;
      if (isSelected) {
        allow = false;
      }
    });

    this.pressureDrops.forEach(pressureDrop => {
      const { isSelected, name } = pressureDrop;
      if (isSelected && name !== 'powerKw') {
        allow = false;
      }
    });

    return allow;
  }

  @computed get selectedMetrics() {
    const deviceMetricsFlatten = flatten(this.devicesMetrics.map(device => device.metrics));
    const selectedDeviceMetrics = filter(deviceMetricsFlatten, { isSelected: true });
    const selectedStationMetrics = filter(this.stationMetrics, { isSelected: true });

    return [...selectedDeviceMetrics, ...selectedStationMetrics];
  }

  @computed get selectedPressureDrops() {
    return filter(this.pressureDrops, { isSelected: true });
  }

  getFirstPeriodSeriesByIndex(index) {
    return this.data1?.data?.[index]?.series;
  }

  getSecondPeriodSeriesByIndex(index) {
    return this.data2?.data?.[index]?.series;
  }

  handleExportEvent(eventPayload) {
    if (eventPayload.status && toUpper(eventPayload.status) === 'FAILED') {
      this.isExportingData = false;
      message.error(i18n.t('Data export failed'));
    } else if (eventPayload.exportFileUrl) {
      this.isExportingData = false;
      download(eventPayload.exportFileUrl);
    }
  }

  @computed get sortedDevicesMetrics() {
    return sortBy(this.devicesMetrics, deviceMetric => deviceMetric?.name?.toLowerCase());
  }

  splitSeriesDataByDevice(deviceId, series) {
    const [off, rest] = partition(series, val => val?.[1] === 'off');
    const [load, rest2] = partition(rest, val => val?.[1] === 'load');
    const [unload] = partition(rest2, val => val?.[1] === 'unload');

    return {
      deviceId,
      length: series.length,
      off: off.length,
      unload: unload.length,
      load: load.length,
    };
  }

  @action getDeviceStatus = async () => {
    const { devices, startDates, endDates } = this.devicesMetricsAggregationDataQueryParams;
    const compressorDevices = devices.filter(device =>
      this.devicesMetrics.some(
        deviceMetric =>
          deviceMetric?.id === device && !!find(deviceMetric.deviceTypes, type => compressorTypes.includes(type)),
      ),
    );

    if (startDates?.[0] && endDates?.[0] && compressorDevices?.length) {
      const { data } = await epaxios.get(
        `investigation-admin/station/${this.rootStore.stationStore.currentStation.id}/activity-metrics?startDate=${
          startDates[0]
        }&endDate=${endDates[0]}&devices=${compressorDevices.join()}`,
      );
      this.deviceStatus = data?.data;

      if (startDates?.[1] && endDates?.[1] && compressorDevices?.length) {
        const { data: data2 } = await epaxios.get(
          `investigation-admin/station/${this.rootStore.stationStore.currentStation.id}/activity-metrics?startDate=${
            startDates[1]
          }&endDate=${endDates[1]}&devices=${compressorDevices.join()}`,
        );
        this.deviceStatus2 = data2?.data;
      }
    }
  };

  @action getPeriodicLogs = async () => {
    try {
      const { data } = await epaxios.get(
        `station-mgmt/periodic-logs/station/${this.rootStore.stationStore.currentStation.id}`,
      );

      this.periodicLogs = data;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Periodic Logs data: ${e}`));
    }
  };

  @action setSelectedInDeviceStatus = (id, value) => {
    const device = find(this.devicesMetrics, { id });
    if (!device) return;

    device.isSelectedInDeviceStatus = value;
  };

  filterAndMapProactiveEngineLogs(data, periodStartDate, periodEndDate) {
    const formattedPeriodStartDate = moment(periodStartDate).utc().valueOf();
    const formattedPeriodEndDate = moment(periodEndDate).utc().valueOf();
    return data.reduce((res, curr) => {
      const currentItem = [];
      const formattedStartDate = moment(curr?.startDate).utc().valueOf();
      const formattedEndDate = moment(curr?.endDate).utc().valueOf();
      if (formattedEndDate <= formattedPeriodStartDate || formattedPeriodStartDate >= formattedPeriodEndDate)
        return res;
      if (formattedStartDate >= formattedPeriodStartDate && formattedEndDate <= formattedPeriodEndDate)
        currentItem.push(curr);
      else if (formattedStartDate <= formattedPeriodStartDate && formattedEndDate <= formattedPeriodEndDate)
        currentItem.push({ ...curr, startDate: periodStartDate });
      else if (formattedStartDate >= formattedPeriodStartDate && formattedEndDate >= formattedPeriodEndDate)
        currentItem.push({ ...curr, endDate: periodEndDate });
      else if (formattedStartDate <= formattedPeriodStartDate && formattedEndDate >= formattedPeriodEndDate)
        currentItem.push({ ...curr, startDate: periodStartDate, endDate: periodEndDate });
      return [...res, ...currentItem];
    }, []);
  }

  @action getProactiveEngineLogs = async () => {
    const { data } = await epaxios.get(
      `station/${this.rootStore.stationStore.currentStation.id}/proactive-engine/logs`,
    );

    const { startDates, endDates } = this.devicesMetricsAggregationDataQueryParams;

    this.proactiveEngineLogs = this.filterAndMapProactiveEngineLogs(data, startDates[0], endDates[0]);
    if (startDates?.[1] && endDates?.[1]) {
      this.proactiveEngineLogs2 = this.filterAndMapProactiveEngineLogs(data, startDates[1], endDates[1]);
    }
  };

  @action getEngineStatusesTimeline = async () => {
    this.cancelEngineStatusesTimelineRequest?.();

    const stationId = this.rootStore.stationStore.currentStation.id;
    if (!stationId) {
      this.engineStatusesTimeline = [];
      return;
    }

    if (!this.hasEngine) {
      this.engineStatusesTimeline = [];
      return;
    }

    const { startDates, endDates } = this.devicesMetricsAggregationDataQueryParams;

    const startDate = startDates?.[0];
    const endDate = endDates?.[0];

    if (!startDate || !endDate) {
      this.engineStatusesTimeline = [];
      return;
    }

    const queryParams = {
      startDate,
      endDate,
    };

    try {
      const { data } = await epaxios.get(`investigation-admin/station/${stationId}/engine-statuses`, {
        params: queryParams,
        paramsSerializer: { serialize: params => qs.stringify(params, { arrayFormat: 'comma' }) },
        cancelToken: new axios.CancelToken(c => {
          this.cancelEngineStatusesTimelineRequest = c;
        }),
      });

      this.engineStatusesTimeline = normalizeResponse(data);
    } catch (e) {
      if (!axios.isCancel(e)) {
        this.engineStatusesTimeline = [];
        Sentry.captureException(e, {
          extra: { stationId, startDate, endDate },
        });
      }
    }
  };

  mapProactiveEngineLogsSeries(series, proactiveEngineLogs) {
    if (series?.length) {
      return series?.map(([time], index) => {
        const state =
          proactiveEngineLogs?.find(
            log =>
              moment(time).utc().isSameOrAfter(moment(log?.startDate).utc()) &&
              moment(time).utc().isSameOrBefore(moment(log?.endDate).utc()),
          )?.newState ?? ProactiveEngineStateEnum?.Activated;

        return {
          name: state,
          color: state === ProactiveEngineStateEnum.Activated ? token.colorSuccess : token.colorError,
          x: moment(time).utc().valueOf(),
          x2: moment(index < series?.length - 1 ? series?.[index + 1]?.[0] : time)
            .utc()
            .valueOf(),
          y: 0,
        };
      });
    }
    return [];
  }

  setProactiveEngineLogsSeries(startDate, endDate, series, proactiveEngineLogs) {
    return !(startDate && endDate) ? [] : this.mapProactiveEngineLogsSeries(series, proactiveEngineLogs);
  }

  getProactiveEngineLogsSeries() {
    const { startDates, endDates } = this.devicesMetricsAggregationDataQueryParams;

    this.proactiveEngineLogsSeries = this.setProactiveEngineLogsSeries(
      startDates?.[0],
      endDates?.[0],
      this.data1?.data?.[this.data1?.data?.length - 1]?.series,
      this.proactiveEngineLogs,
    );

    this.proactiveEngineLogsSeries2 = this.setProactiveEngineLogsSeries(
      startDates?.[1],
      endDates?.[1],
      this.data2?.data?.[this.data1?.data?.length - 1]?.series,
      this.proactiveEngineLogs2,
    );

    this.recalculateEngineStateChangePoints();
  }

  @action recalculateEngineStateChangePoints() {
    const { startDates, endDates } = this.devicesMetricsAggregationDataQueryParams;

    const mappedTimelineLogs = (this.engineStatusesTimeline ?? []).map(item => ({
      startDate: item.startDate,
      newState: item.state,
    }));

    this.engineStateChangePoints = getEngineStateChangePoints(mappedTimelineLogs, startDates?.[0], endDates?.[0]);
  }

  arePeriodAndEventOverlapping = (periodStartDate, periodEndDate, eventStartDate, eventEndDate) => {
    const dataItemStartDate = moment(eventStartDate).utc();
    const dataItemEndDate = moment(eventEndDate).utc();
    return (
      (periodStartDate?.isSameOrAfter(dataItemStartDate) &&
        (periodEndDate === null || periodEndDate?.isSameOrBefore(dataItemEndDate))) ||
      (dataItemStartDate?.isSameOrAfter(periodStartDate) && dataItemEndDate?.isSameOrBefore(periodEndDate))
    );
  };

  getDevicesStatusSeries() {
    if (this.deviceStatus?.some(compressor => compressor?.series?.length)) {
      this.deviceStatusSeries = this.deviceStatus?.map((compressor = { series: [] }) => ({
        ...compressor,
        series: flatten(
          compressor.series.map(([time, status], index) => {
            const startDate = moment(time).utc();
            const endDate = moment(
              index < compressor?.series?.length - 1 ? compressor?.series?.[index + 1]?.[0] : time,
            ).utc();

            let alertStatus;
            const overlappedAlert = this.sortedAlertsByShutdown?.find(
              alert =>
                alert?.affectedDevicesWithAttributes?.some(device => device?.id === compressor?.deviceId) &&
                this.arePeriodAndEventOverlapping(startDate, endDate, alert?.openingTime, alert?.lastUpdated),
            )?.severity;
            if (overlappedAlert === AlertLevels.Shutdown || overlappedAlert === AlertLevels.Warning)
              alertStatus = overlappedAlert;
            else {
              const periodicLogEventType = this.periodicLogs?.find(
                periodicLog =>
                  periodicLog?.deviceId === compressor?.deviceId &&
                  this.arePeriodAndEventOverlapping(startDate, endDate, periodicLog?.startedAt, periodicLog?.endedAt),
              )?.eventType;
              if (periodicLogEventType === 'inMaintenance') alertStatus = InsightType.Maintenance;
            }

            return [
              {
                name: upperFirst(t(status)),
                color: getSeriesColorMapping(token)[status ?? 'n/a'],
                x: startDate.valueOf(),
                x2: endDate.valueOf(),
                y: 0,
              },
              ...(alertStatus
                ? [
                    {
                      name: upperFirst(t(alertStatus)),
                      color: getSeriesColorMapping(token)[alertStatus ?? 'n/a'],
                      x: startDate.valueOf(),
                      x2: endDate.valueOf(),
                      y: 0,
                    },
                  ]
                : [
                    {
                      name: noAlert,
                      color: 'transparent',
                      x: startDate.valueOf(),
                      x2: endDate.valueOf(),
                      y: 0,
                    },
                  ]),
            ];
          }),
        ),
      }));
    } else this.deviceStatusSeries = [];
  }

  getFilteredDevicesStatusSeries(compressorId, withAlert?) {
    const compressor = find(this.deviceStatusSeries, { deviceId: compressorId });
    const [withAlertStatus, withoutAlertStatus] = partition(
      compressor?.series,
      series => series?.name === noAlert || compressorAlertStatus.includes(series?.name),
    );
    return withAlert ? withAlertStatus : withoutAlertStatus;
  }

  @computed get isAnyCompressorSelectedInDeviceStatus() {
    return some(this.devicesMetrics, device => device.isSelectedInDeviceStatus);
  }

  @computed get hasSeries() {
    return (!isEmpty(this.selectedMetrics) || !isEmpty(this.selectedPressureDrops)) && !isEmpty(this.dates1);
  }

  @computed get isReady() {
    const metricsArraysExist =
      Array.isArray(this.devicesMetrics) && Array.isArray(this.stationMetrics) && Array.isArray(this.pressureDrops);

    if (!metricsArraysExist) {
      return false;
    }

    const isStationLoaded = !this.rootStore.stationStore.isLoading && !!this.rootStore.stationStore.currentStation.id;

    if (!isStationLoaded) {
      return false;
    }

    return Array.isArray(this.rootStore.devicesStore.pressureDropDevices);
  }
}

/*device metrics aggregations*/

class DeviceMetricsAggregationData {
  @observable dates = {};
  @observable stationTotalRow: { rowData? } = {};
  @observable devicesBreakdownRow: { rowData? } = {};

  constructor(data) {
    makeObservable(this);
    const shouldReverseDates = this.getShouldReverseDates(data.dates);
    this.dates = shouldReverseDates ? this.reverseDates(data.dates) : data.dates;
    this.stationTotalRow = new DiffRow(
      data.stationTotal,
      shouldReverseDates,
      this.dates,
      'stationTotal',
      'Station KPIs',
    );
    this.devicesBreakdownRow = new DevicesBreakdownRow(data.devices, shouldReverseDates, this.dates);
  }

  reverseDates(dates) {
    if (!dates) return;

    const { period1, period2 } = dates;

    return {
      period1: period2,
      period2: period1,
    };
  }

  getShouldReverseDates(dates) {
    if (!dates.period2) return false;

    const isPeriod1BeforePeriod2 = moment(dates.period1.startDate).isBefore(dates.period2.startDate);

    return isPeriod1BeforePeriod2;
  }

  @computed get rowsData() {
    const rowsData = [];

    if (!isEmpty(this.stationTotalRow.rowData)) {
      rowsData.push(this.stationTotalRow.rowData);
    }

    if (!isEmpty(this.devicesBreakdownRow.rowData)) {
      rowsData.push(this.devicesBreakdownRow.rowData);
    }

    return rowsData;
  }
}

const isChildOfProductionFloorFunc = key => device =>
  device.deviceType === DeviceType.ProductionFloor && device.onDevices.includes(key);

class DevicesBreakdownRow {
  @observable key;
  @observable devicesDiffRows;

  constructor(data, shouldReverseDates, dates) {
    makeObservable(this);
    this.key = 'devicesBreakdown';
    const devicesGroupedById = groupBy(data, 'deviceId');
    this.devicesDiffRows = Object.entries(devicesGroupedById).map(([key, value]) => {
      const parentProductionFloor = RootStore.stationStore.currentStation.devices.find(
        isChildOfProductionFloorFunc(key),
      );
      const deviceName = `${parentProductionFloor?.name ? `${parentProductionFloor?.name} - ` : ''}${
        RootStore.devicesStore.deviceMetadataFromDeviceId(key)?.name
      }`;
      return new DiffRow(value, shouldReverseDates, dates, `devicesBreakdown_${key}`, deviceName);
    });
    this.devicesDiffRows = sortBy(this.devicesDiffRows, ({ diffRow }) => {
      const key = diffRow?.key?.replace('devicesBreakdown_', '');
      const isProductionFloor = RootStore.stationStore.currentStation.devices.some(
        device => device.id === key && device.deviceType === DeviceType.ProductionFloor,
      );
      const isChildOfProductionFloor = RootStore.stationStore.currentStation.devices.some(
        isChildOfProductionFloorFunc(key),
      );

      return isProductionFloor ? 1 : isChildOfProductionFloor ? 0 : -1;
    });
  }

  @computed get rowData() {
    let rowData: { key?; title?; children? } = {};

    this.devicesDiffRows.forEach(diffRow => {
      if (!isEmpty(diffRow.rowData)) {
        rowData = {
          key: this.key,
          title: 'Devices breakdown',
          children: [...(rowData.children || []), diffRow.rowData],
        };
      }
    });

    return rowData;
  }
}

class DiffRow {
  @observable title;
  @observable period1Row: { rowData? } = {};
  @observable period2Row: { rowData? } = {};
  @observable diffRow: { rowData? } = {};

  constructor(data, shouldReverseDates, dates, key, title) {
    makeObservable(this);
    this.title = title;
    this.diffRow = new Row(data, shouldReverseDates, dates, key, title, 'difference');
    this.period1Row = new Row(
      data,
      shouldReverseDates,
      dates,
      `${key}_period1`,
      `${moment(dates.period1.startDate).format('D.M.YY')} - ${moment(dates.period1.endDate).format('D.M.YY')}`,
      'period1',
    );
    this.period2Row = new Row(
      data,
      shouldReverseDates,
      dates,
      `${key}_period2`,
      `${moment(dates.period2?.startDate).format('D.M.YY')} - ${moment(dates.period2?.endDate).format('D.M.YY')}`,
      'period2',
    );
  }

  @computed get rowData() {
    let rowData = {};

    if (!isEmpty(this.period2Row.rowData)) {
      rowData = {
        subtitle: 'Periods compare',
        ...this.diffRow.rowData,
        children: [this.period1Row.rowData, this.period2Row.rowData],
      };
    } else if (!isEmpty(this.period1Row.rowData)) {
      rowData = {
        subtitle: this.title,
        ...this.period1Row.rowData,
      };
    }

    return rowData;
  }
}

class Row {
  @observable dates;
  @observable key;
  @observable title;
  @observable metrics = [];

  constructor(data, shouldReverseDates, dates, key, title, aggregationKey) {
    makeObservable(this);
    if (!data) return;

    this.dates = dates;
    this.key = key;
    this.title = title;
    this.metrics = data.map(metric => new Metric(metric, shouldReverseDates, aggregationKey));
  }

  @computed get rowData() {
    let rowData = {};

    this.metrics.forEach(metric => {
      if (!isEmpty(metric.rowData)) {
        rowData = {
          ...rowData,
          key: this.key,
          title: this.title,
          ...metric.rowData,
        };
      }
    });

    return rowData;
  }
}

class Metric {
  @observable metric;
  @observable aggregationKey;

  constructor(data, shouldReverseDates, aggregationKey) {
    makeObservable(this);
    this.aggregationKey = this.getAggregationKey(aggregationKey, data.metric);
    this.metric = shouldReverseDates
      ? {
          ...data,
          aggregations: {
            ...data.aggregations,
            period1: data.aggregations.period2,
            period2: data.aggregations.period1,
          },
        }
      : data;
  }

  @computed get rowData() {
    const rowData = {};
    const metricName = this.metric.metric;

    Object.entries(this.metric.aggregations[this.aggregationKey] || {}).forEach(([key, value]) => {
      const valueCasted =
        this.aggregationKey === 'relativeDifference'
          ? numeral(value).format('0,0[.]0%')
          : numeral(value).format('0,0[.]00');

      rowData[`${metricName}_${key}`] = valueCasted;
    });

    return rowData;
  }

  getAggregationKey(key, metricName) {
    if (!['difference', 'relativeDifference'].includes(key)) return key;

    switch (metricName) {
      case 'pressureBar':
        return 'difference';

      default:
        return 'relativeDifference';
    }
  }
}
